import { BoxProps, SelectOption } from '@dtx-company/shared-components/src'
import {
  HandlingOption,
  Maybe,
  PriceTier,
  PriceType,
  Product,
  ShippingOption,
  ShoppingCartItem
} from '@dtx-company/flow-codegen/src/code/generated.types'
import { LandingPageDestination } from '@dtx-company/inter-app/src/types/flowcode'
import { PlacementTemplateProduct } from './placement-template.types'
import { ReactNode } from 'react'

export interface SelectedBillingOptionsProps {
  quantity: PriceTier['quantity']
  savedValue: number
  country: SelectOption
  shippingOptionId: ShippingOption['id']
  // TODO: remove shippingCost from this interface
  shippingCost: ShippingOption['price']
  // TODO: make this not optional once quicker delivery feature is fully live
  handlingOptionId?: HandlingOption['id']
  unitAmount: PriceTier['unitAmount']
  itemsSubTotal: string
  businessName?: string
  itemsSubTotalDiscounted: number
}

export enum ProductTypeName {
  BUSINESS_CARDS = 'Business Cards',
  TABLE_TENTS = 'Table tents',
  COASTERS = 'Coasters',
  STICKERS = 'Stickers',
  PREDESIGN_FLOWTAGS = 'Predesigned Flowtags'
}

export enum PrintStorePriceType {
  FIXED = 'FIXED'
  // In the future there can be more like "step" or "linear".
}

export interface PrintStoreProductsType {
  id: string
  name: string
  imageUrls: string[]
  availableQuantity: number
  isCustomizable: boolean
  priceModel: {
    priceType: PriceType
    sortedTiers: {
      quantity: number
      unitAmount: number
    }[]
  }
}

export interface GetPrintStoreProductsAndBatchResponseType {
  printStoreProducts: {
    products: PrintStoreProductsType[]
  }
  batch: {
    id: string
    nickname: string
  }
}

export interface GetPrintStoreProductsResponseType {
  printStoreProducts: {
    products: PrintStoreProductsType[]
  }
}

export interface PrintStoreProductItemsProps extends BoxProps {
  products?: PrintStoreProductsType[]
  isLoading: boolean
  batchId?: string | string[]
}

export interface ProductCardProps {
  name: string
  description?: React.ReactNode
  imageUrl: string
  actionButtonLabel: string
  isDisabled?: boolean
  addBorder?: boolean
}

export interface SurveyCardDataProps {
  id: string
  name: string
  description: string
  imageUrl: string
  actionButtonLabel: string
  link: string
}

export interface PredesignedFlowtagProps {
  id: string
  name: string
  imageUrl: string
  link: string
  actionButtonLabel: string
  productType: ProductTypeName
}

export interface PrintStoreProductDetailsProps extends PrintStoreProductsType {
  maxOrderQuantity: number
  orderQuantityIncrement: number
  currency: string
  priceId: string
  description: string
  headerText: string
  handlingTimeBusinessDays: number
}

export interface PrintStoreProductShippingOptionsProps {
  id: string
  name: string
  description: string
  deliveryDaysRangeMin: number
  deliveryDaysRangeMax: number
  doesSupportInternational: boolean
  excludedDeliveryDaysOfWeek: number[]
  currency: string
  price: number
}

export interface ProductDetailsProps {
  product: Product
  shippingOptions: ShippingOption[]
}

export interface GetPrintStoreProductDetailsResponseType {
  printStoreProduct: ProductDetailsProps
  batch: {
    id: string
    nickname: string
    activeLink: { shorturl: string; redirectValue: string; redirectType: LandingPageDestination }
    studioConfig: {
      id: string
      configuration: string
    }
  }
}

export interface ProductPreviewProps {
  name: string
  imageUrl: string
  batchName: string
  batchConfiguration?: string | null
  productId: string
  selectedBillingOptions: SelectedBillingOptionsProps
  batch: GetPrintStoreProductDetailsResponseType['batch']
}

export interface WhyPurchaseInfoDataProps {
  iconSrc: string
  title: string
  description: string
}

export interface HandleInputChangeProps {
  quantity?: PriceTier['quantity']
  savedValue?: number
  country?: SelectOption
  shippingOptionId?: ShippingOption['id']
  shippingCost?: ShippingOption['price']
  handlingOptionId?: HandlingOption['id']
  handlingCost?: number
  unitAmount?: PriceTier['unitAmount']
  itemsSubTotal?: string
  businessName?: string
}

export interface ProductBillingProps {
  productDetails: ProductDetailsProps
  selectedBillingOptions: SelectedBillingOptionsProps
  setSelectedBillingOptions: React.Dispatch<React.SetStateAction<SelectedBillingOptionsProps>>
  isSubmitting: boolean
  onAddToCart: () => void
  templateProducts: PlacementTemplateProduct[]
  productId: string
  batchId: string
  batchName: string
}

export interface ProductQuantityProps {
  handleInputChange: (input: HandleInputChangeProps) => void
  disabled: boolean
  title?: string
  width?: BoxProps['width']
  dropdownMenuWidth?: BoxProps['width']
  product: ProductDetailsProps['product']
  quantity: ShoppingCartItem['quantity']
  dropdownMenuItemsProps?: BoxProps
}

export interface ProductShippingProps
  extends Omit<
    ProductBillingProps,
    | 'setSelectedBillingOptions'
    | 'onCheckout'
    | 'isSubmitting'
    | 'setIsCheckoutButtonDisabled'
    | 'isCheckoutButtonDisabled'
    | 'templateProducts'
    | 'productId'
    | 'batchId'
    | 'batchName'
  > {
  handleInputChange: (input: HandleInputChangeProps) => void
  disabled: boolean
}

export interface UsePrintProductDetailsProps {
  productDetails?: ProductDetailsProps
}

export interface UsePrintProductDetailsReturnType {
  initialBillingOptions?: SelectedBillingOptionsProps
}

export interface UseProductDeliveryDateRangeProps {
  getEstimatedDeliveryDates: (
    deliveryDaysRangeMin: ShippingOption['deliveryDaysRangeMin'],
    deliveryDaysRangeMax: ShippingOption['deliveryDaysRangeMax'],
    basicProcessingTime: Product['handlingTimeBusinessDays'],
    nonWorkingDays?: ShippingOption['excludedDeliveryDaysOfWeek']
  ) => string
  getEstimatedDeliveryDatesFromUTC: (
    estimatedDeliveryDateMin: ShippingOption['estimatedDeliveryDateMin'],
    estimatedDeliveryDateMax: ShippingOption['estimatedDeliveryDateMax']
  ) => string
}

export enum PrintStoreCheckoutTypes {
  SUCCESS = 'success',
  ERROR = 'error'
}

export interface PrintStoreCheckoutModalProps {
  open: boolean
  onClose: () => void
  checkoutResult: string
}

export interface GetPriceForQuantityResponse {
  price: string
  unitAmount: PriceTier['unitAmount']
}

export interface ProductQuantityItemsProps {
  tier: Maybe<PriceTier>
  handleQuantity: (quantity?: PriceTier['quantity'], savedValue?: number) => void
  selectedQuantity: PriceTier['quantity']
  minUnitAmount: PriceTier['unitAmount']
}

export interface PrintStoreLandingPageProps {
  products?: PrintStoreProductsType[]
  isLoading: boolean
}

export interface FetchPrintStoreDiscountsResponseType {
  printStoreDiscountsByPlan: {
    freeDiscount: { percent: number }
    proDiscount: { percent: number }
    proplusDiscount: { percent: number }
    proflexDiscount: { percent: number }
    enterpriseDiscount: { percent: number }
  }
}

export interface WrapperProps {
  children?: ReactNode
}
